import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
	static targets = ['container', 'item', 'toggle', 'toggleWrap', 'count', 'icon'];

	connect() {
		this.expanded = false;
		this.onResize = this.onResize.bind(this);

		if (typeof ResizeObserver !== 'undefined' && this.hasContainerTarget) {
			this.ro = new ResizeObserver(this.onResize);
			this.ro.observe(this.containerTarget);
		} else {
			window.addEventListener('resize', this.onResize, { passive: true });
		}

		// Recompute when a chip is toggled: checking adds the "x" (pr-11) which changes a chip's
		// width and rewraps items WITHOUT changing the container's own box size, so the container
		// ResizeObserver alone misses the shrink-back on uncheck.
		this.onCategoryChange = this.onCategoryChange.bind(this);
		this.element.addEventListener('change', this.onCategoryChange);

		// wait for layout + fonts
		requestAnimationFrame(() => requestAnimationFrame(() => this.update()));
	}

	disconnect() {
		this.ro?.disconnect?.();
		window.removeEventListener?.('resize', this.onResize);
		this.element.removeEventListener('change', this.onCategoryChange);
	}

	onResize() {
		// wait for layout
		requestAnimationFrame(() => this.update());
	}

	onCategoryChange() {
		requestAnimationFrame(() => this.update());
	}

	toggle() {
		this.expanded = !this.expanded;
		this.element.classList.toggle('is-expanded', this.expanded);
		this.update();
	}

	update() {
		const items = this.itemTargets.filter((el) => el && el instanceof HTMLElement);
		if (items.length === 0) {
			this.expanded = false;
			this.element.classList.remove('is-expanded');
			if (this.hasToggleTarget) {
				this.toggleTarget.setAttribute('aria-expanded', 'false');
			}
			this.setHiddenCount(0);
			this.hideToggle();
			return;
		}

		// Reveal every item so the natural (wrapped) layout can be measured, even while expanded —
		// this way, once enough chips shrink/deselect to fit one row, the toggle can disappear.
		items.forEach((el) => {
			el.hidden = false;
		});

		const containerRect = this.hasContainerTarget ? this.containerTarget.getBoundingClientRect() : null;
		const measured = items.map((el) => {
			const rect = el.getBoundingClientRect();
			const top = containerRect ? Math.round(rect.top - containerRect.top) : Math.round(rect.top);
			return { el, top };
		});
		const firstTop = measured[0]?.top ?? 0;

		let hiddenCount = 0;
		measured.forEach(({ el, top }) => {
			const inFirstRow = Math.abs(top - firstTop) <= 1;
			if (!inFirstRow) hiddenCount += 1;
			// Keep overflow items visible while expanded; hide them only when collapsed.
			el.hidden = this.expanded ? false : !inFirstRow;
		});

		if (hiddenCount > 0) {
			if (this.hasToggleTarget) {
				this.toggleTarget.setAttribute('aria-expanded', this.expanded ? 'true' : 'false');
			}
			this.setHiddenCount(hiddenCount);
			this.showToggle();
		} else {
			this.expanded = false;
			this.element.classList.remove('is-expanded');
			if (this.hasToggleTarget) {
				this.toggleTarget.setAttribute('aria-expanded', 'false');
			}
			this.setHiddenCount(0);
			this.hideToggle();
		}
	}

	setHiddenCount(count) {
		if (!this.hasCountTarget) return;
		this.countTarget.textContent = count > 0 ? `(+${count})` : '';
	}

	showToggle() {
		if (this.hasToggleWrapTarget) this.toggleWrapTarget.classList.remove('hidden');
		if (this.hasToggleTarget) this.toggleTarget.classList.remove('hidden');
	}

	hideToggle() {
		if (this.hasToggleWrapTarget) this.toggleWrapTarget.classList.add('hidden');
		if (this.hasToggleTarget) this.toggleTarget.classList.add('hidden');
	}
}

